import { gql } from '@apollo/client';
import type { MutationTuple } from '@apollo/client';
import { useState, useRef, useEffect } from 'react';
import type { DownloadStatus } from '../types';
import usePolledQuery from './apollo/usePolledQuery';

const useFileDownload = <TData, TVariable>(
    [
        initiateDownload,
        { data: initiateDownloadData, loading, reset: resetMutation, error },
    ]: MutationTuple<TData, TVariable>,
    variables: TVariable,
    getDownloadIdFromResult: (data?: TData) => string | undefined
) => {
    const [requestFailed, setRequestFailed] = useState(false);
    const isDownloadLinkOpened = useRef(false);

    const id = getDownloadIdFromResult(initiateDownloadData || undefined);

    const {
        data,
        loading: downloadLoading,
        stopPolling,
        startPolling,
        error: downloadRequestStatusError,
    } = usePolledQuery<{
        downloadRequestStatus: {
            id: string;
            status: DownloadStatus;
            url?: string;
        };
    }>(
        gql`
            query downloadRequestStatus($id: ID!) {
                downloadRequestStatus(id: $id) {
                    id
                    status
                    url
                }
            }
        `,
        {
            variables: {
                id,
            },
            skip: !id || loading,
        }
    );

    useEffect(() => {
        if (error || downloadRequestStatusError) {
            setRequestFailed(true);
            stopPolling();
        }
    }, [downloadRequestStatusError, error, stopPolling]);

    useEffect(() => {
        if (data?.downloadRequestStatus.status === 'failed') {
            setRequestFailed(true);
            stopPolling();
        }

        if (data?.downloadRequestStatus.status === 'ready') {
            const { url } = data.downloadRequestStatus;
            resetMutation();
            stopPolling();
            window.open(url, '_self');
        }
    }, [data?.downloadRequestStatus, resetMutation, stopPolling]);

    const startDownload = async (overrideVariables?: TVariable) => {
        isDownloadLinkOpened.current = false;
        setRequestFailed(false);
        startPolling(5_000);

        await initiateDownload({
            variables: {
                ...variables,
                ...overrideVariables,
            },
        });
    };

    const reset = () => {
        resetMutation();
        setRequestFailed(false);
    };

    return {
        startDownload,
        requestFailed,
        loading:
            downloadLoading ||
            data?.downloadRequestStatus.status === 'pending' ||
            loading,
        downloadRequestStatus: data?.downloadRequestStatus.status,
        reset,
    };
};

export default useFileDownload;
